<template>
  <!-- The v2 Portfolio page's projects (S5_Index, workstream PX): one of five
       layouts (even grid, masonry, justified rows, list with the photo on
       hover, big slideshow) with type filter chips. Cards come resolved from
       the server (utils/portfolioV2.js). Without JavaScript every card shows
       and the chips are plain buttons; filtering only hides cards. -->
  <section class="t-band px-port" :data-layout="layout" aria-label="Projects">
    <div class="t-band__inner">
      <div v-if="chips.length" class="px-port__chips" role="group" aria-label="Show only">
        <button type="button" class="px-port__chip" :aria-pressed="active === '' ? 'true' : 'false'" @click="active = ''">All</button>
        <button v-for="c in chips" :key="c.key" type="button" class="px-port__chip"
                :aria-pressed="active === c.key ? 'true' : 'false'" @click="active = c.key">{{ c.label }}</button>
      </div>
      <p v-if="!shown.length" class="t-muted px-port__empty">{{ cards.length ? 'Nothing here yet.' : 'Projects will appear here soon.' }}</p>

      <!-- Big slideshow: one project at a time, swipe or arrows. -->
      <div v-else-if="layout === 'slideshow'" class="px-slides">
        <ul ref="track" class="px-slides__track" tabindex="0" aria-label="Projects, scroll sideways" @scroll.passive="onScroll">
          <li v-for="(c, i) in shown" :key="c.slug" class="px-slides__item" :aria-label="`${i + 1} of ${shown.length}`">
            <a :href="back(c.link)" class="px-slides__card">
              <span class="px-slides__photo"><img v-if="c.cover" :src="c.cover" v-bind="imgAttrs(c.cover, '(min-width: 1024px) 33vw, (min-width: 640px) 50vw, 100vw')" :alt="c.title" class="t-photo" loading="lazy" decoding="async"></span>
              <span class="px-slides__words">
                <span v-if="meta(c)" class="t-eyebrow">{{ meta(c) }}</span>
                <strong class="t-h2 px-slides__title">{{ c.title }}</strong>
                <span v-if="c.intro" class="t-muted px-slides__intro">{{ c.intro }}</span>
                <Stars v-if="c.stars" :n="c.stars" />
                <span class="t-link">See the project</span>
              </span>
            </a>
          </li>
        </ul>
        <div v-if="shown.length > 1" class="px-slides__nav">
          <button type="button" class="px-slides__btn" aria-label="Previous project" @click="step(-1)">‹</button>
          <span class="t-muted" aria-live="polite">{{ current + 1 }} / {{ shown.length }}</span>
          <button type="button" class="px-slides__btn" aria-label="Next project" @click="step(1)">›</button>
        </div>
      </div>

      <!-- List with the photo on hover (architects): rows of words; the photo
           follows on a computer, sits beside the row on a phone. -->
      <ol v-else-if="layout === 'list'" class="px-list mo-stagger" @mouseleave="hover = ''">
        <li v-for="c in shown" :key="c.slug" class="px-list__row mo-reveal">
          <a :href="back(c.link)" class="px-list__link" @mouseenter="peek(c.slug, $event)" @focus="peek(c.slug, $event)">
            <span class="px-list__thumb"><img v-if="c.cover" :src="c.cover" v-bind="imgAttrs(c.cover, '(min-width: 1024px) 33vw, (min-width: 640px) 50vw, 100vw')" alt="" class="t-photo" loading="lazy" decoding="async"></span>
            <strong class="px-list__title">{{ c.title }}</strong>
            <span class="t-muted px-list__meta">{{ meta(c) }}</span>
            <Stars v-if="c.stars" :n="c.stars" />
          </a>
        </li>
        <li aria-hidden="true" class="px-list__peek" :class="{'px-list__peek--on': hoverCard}" :style="{top: `${hoverTop}px`}">
          <img v-if="hoverCard && hoverCard.cover" :src="hoverCard.cover" v-bind="imgAttrs(hoverCard.cover, '(min-width: 1024px) 33vw, (min-width: 640px) 50vw, 100vw')" alt="" class="t-photo">
        </li>
      </ol>

      <!-- Grid, masonry, justified rows: the same card, laid out differently. -->
      <ul v-else class="px-cards mo-stagger" :class="`px-cards--${layout}`" :data-count="shown.length > 2 ? 'many' : shown.length">
        <li v-for="(c, i) in shown" :key="c.slug" class="px-cards__item mo-reveal" :style="layout === 'justified' ? {flexGrow: grow(i)} : null">
          <a :href="back(c.link)" class="px-card t-card">
            <span v-if="c.pair" class="px-card__pair" :aria-label="`Before and after: ${c.title}`">
              <span class="px-card__half"><img :src="c.pair.before" v-bind="imgAttrs(c.pair.before, '(min-width: 1024px) 33vw, (min-width: 640px) 50vw, 100vw')" alt="" loading="lazy" decoding="async"><span class="t-badge px-card__tag">Before</span></span>
              <span class="px-card__half"><img :src="c.pair.after" v-bind="imgAttrs(c.pair.after, '(min-width: 1024px) 33vw, (min-width: 640px) 50vw, 100vw')" alt="" loading="lazy" decoding="async"><span class="t-badge px-card__tag">After</span></span>
            </span>
            <span v-else class="px-card__photo" :class="`px-card__photo--${layout === 'masonry' ? (i % 3 === 1 ? 'tall' : i % 3 === 2 ? 'square' : 'wide') : 'even'}`">
              <img v-if="c.cover" :src="c.cover" v-bind="imgAttrs(c.cover, '(min-width: 1024px) 33vw, (min-width: 640px) 50vw, 100vw')" :alt="c.title" class="t-photo" loading="lazy" decoding="async">
            </span>
            <span class="px-card__words">
              <strong class="t-h3 px-card__title">{{ c.title }}</strong>
              <span v-if="meta(c)" class="t-muted px-card__meta">{{ meta(c) }}</span>
              <Stars v-if="c.stars" :n="c.stars" />
            </span>
          </a>
        </li>
      </ul>
    </div>
  </section>
</template>

<script setup>
import {imgAttrs} from "@/utils/photoSizes.js";
import {computed, h, ref} from "vue";
import {useBackLinks} from "@/composables/useBackLinks.js";
const back = useBackLinks();

const props = defineProps({
  section: {type: Object, required: true},
  variant: {type: [Number, String], default: 1},
});
const LAYOUTS = ["grid", "masonry", "justified", "list", "slideshow"];
const layout = computed(() => (LAYOUTS.includes(props.section.layout) ? props.section.layout : "grid"));
const cards = computed(() => (Array.isArray(props.section.cards) ? props.section.cards.filter((c) => c && c.slug) : []));
const chips = computed(() => (Array.isArray(props.section.chips) ? props.section.chips : []));
const active = ref("");
const shown = computed(() => (active.value ? cards.value.filter((c) => (c.keys || []).includes(active.value)) : cards.value));
const meta = (c) => [c.type, c.place].filter(Boolean).join(" · ");

// Justified rows: varied widths so rows read like a contact sheet.
const GROW = [3, 2, 2, 3, 2, 3, 3, 2];
const grow = (i) => GROW[i % GROW.length];

const hover = ref("");
const hoverTop = ref(0);
function peek(slug, e) {
  hover.value = slug;
  const row = e?.currentTarget?.closest?.("li");
  if (row) hoverTop.value = Math.max(0, row.offsetTop - 40);
}
const hoverCard = computed(() => cards.value.find((c) => c.slug === hover.value) || null);

const track = ref(null);
const current = ref(0);
function onScroll() {
  const el = track.value;
  if (el && el.clientWidth) current.value = Math.round(el.scrollLeft / el.clientWidth);
}
function step(d) {
  const el = track.value;
  if (!el) return;
  const reduced = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
  const next = Math.max(0, Math.min(shown.value.length - 1, current.value + d));
  el.scrollTo({left: next * el.clientWidth, behavior: reduced ? "auto" : "smooth"});
  current.value = next;
}

// Stars from the linked review.
const Stars = (p) => h("span", {class: "px-stars", "aria-label": `${p.n} out of 5 stars`},
  "★★★★★".slice(0, p.n) + "☆☆☆☆☆".slice(0, 5 - p.n));
Stars.props = ["n"];
</script>

<style scoped>
.px-port__chips { display: flex; flex-wrap: wrap; gap: .5rem; margin-bottom: clamp(1.25rem, 3vw, 2rem); }
.px-port__chip { min-height: 44px; padding: .45rem 1rem; border-radius: var(--t-btn-radius, 999px); border: 1.5px solid var(--t-line); background: var(--t-surface); color: var(--t-ink); font: 600 .9rem var(--t-font-body); cursor: pointer; }
.px-port__chip[aria-pressed="true"] { background: var(--t-btn-bg, var(--t-ink)); color: var(--t-btn-ink, var(--t-bg)); border-color: var(--t-btn-bg, var(--t-ink)); }
.px-port__chip:focus-visible, .px-card:focus-visible, .px-list__link:focus-visible, .px-slides__card:focus-visible { outline: 3px solid color-mix(in srgb, var(--t-accent) 55%, transparent); outline-offset: 3px; }
.px-port__empty { padding: 2rem 0; }
.px-stars { color: var(--t-accent); letter-spacing: .1em; font-size: .9rem; }

/* Cards */
.px-cards { list-style: none; margin: 0; padding: 0; }
.px-cards--grid { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: var(--t-gap); }
.px-cards--masonry { columns: 3; column-gap: var(--t-gap); }
.px-cards--masonry .px-cards__item { break-inside: avoid; margin-bottom: var(--t-gap); }
.px-cards--justified { display: flex; flex-wrap: wrap; gap: var(--t-gap); }
.px-cards--justified .px-cards__item { flex-basis: 260px; min-width: 0; }
.px-card { display: flex; flex-direction: column; height: 100%; color: inherit; text-decoration: none; overflow: hidden; padding: 0; }
/* The card is the frame: the photo fills it edge to edge, without the theme's
   photo border or shadow on top (that read as stray marks at the corners). */
.px-card .t-photo, .px-slides__photo .t-photo { border: 0; box-shadow: none; transform: none; border-radius: 0; }
/* Fewer than three projects: two columns for two, one wide feature card for one. */
.px-cards--grid[data-count="2"] { grid-template-columns: repeat(2, minmax(0, 1fr)); }
.px-cards--grid[data-count="1"], .px-cards--masonry[data-count="1"], .px-cards--justified[data-count="1"] { display: grid; grid-template-columns: minmax(0, 1fr); max-width: 980px; margin-inline: auto; columns: auto; }
.px-cards--grid[data-count="1"] .px-card { flex-direction: row; align-items: stretch; }
.px-cards--grid[data-count="1"] .px-card__photo, .px-cards--grid[data-count="1"] .px-card__pair { flex: 1.5 1 0; aspect-ratio: 4 / 3; }
.px-cards--grid[data-count="1"] .px-card__words { flex: 1 1 0; justify-content: center; padding: clamp(1.25rem, 3vw, 2.5rem); }
.px-cards--grid[data-count="1"] .px-card__title { font-size: var(--t-h2); }
.px-cards--masonry[data-count="2"] { columns: 2; }
.px-card__photo { display: block; overflow: hidden; background: var(--t-surface-2); }
.px-card__photo img { display: block; width: 100%; height: 100%; object-fit: cover; transition: transform .5s ease; }
.px-card__photo--even { aspect-ratio: 4 / 3; }
.px-card__photo--wide { aspect-ratio: 4 / 3; }
.px-card__photo--tall { aspect-ratio: 3 / 4; }
.px-card__photo--square { aspect-ratio: 1 / 1; }
.px-cards--justified .px-card__photo { aspect-ratio: auto; height: 240px; }
.px-card:hover .px-card__photo img { transform: scale(1.03); }
.px-card__pair { display: grid; grid-template-columns: 1fr 1fr; gap: 2px; aspect-ratio: 4 / 3; overflow: hidden; }
.px-card__half { position: relative; overflow: hidden; }
.px-card__half img { width: 100%; height: 100%; object-fit: cover; display: block; }
.px-card__tag { position: absolute; left: .5rem; bottom: .5rem; }
.px-card__words { display: flex; flex-direction: column; gap: .25rem; padding: var(--t-card-padding, 1rem); }
.px-card__title { margin: 0; }
.px-card__meta { font-size: .9rem; }

/* List with the photo on hover */
.px-list { list-style: none; margin: 0; padding: 0; position: relative; border-top: 1px solid var(--t-line); }
.px-list__row { border-bottom: 1px solid var(--t-line); }
.px-list__link { display: grid; grid-template-columns: 1fr auto auto; align-items: baseline; gap: 1rem; padding: 1.1rem 0; color: inherit; text-decoration: none; min-height: 44px; }
.px-list__thumb { display: none; }
.px-list__title { font: var(--t-display-weight, 700) clamp(1.15rem, 2.4vw, 1.6rem)/1.2 var(--t-font-display); text-transform: var(--t-display-case, none); }
.px-list__link:hover .px-list__title { color: var(--t-accent); }
.px-list__peek { position: absolute; right: 0; top: 0; width: 34%; pointer-events: none; opacity: 0; transition: opacity .25s ease, top .25s ease; }
.px-list__peek--on { opacity: 1; }
.px-list__peek img { display: block; width: 100%; aspect-ratio: 4 / 3; object-fit: cover; box-shadow: var(--t-shadow); }
.px-port[data-layout="list"] .px-list__link { padding-right: 36%; }

/* Big slideshow */
.px-slides__track { list-style: none; margin: 0; padding: 0; display: grid; grid-auto-flow: column; grid-auto-columns: 100%; overflow-x: auto; scroll-snap-type: x mandatory; scrollbar-width: none; }
.px-slides__track::-webkit-scrollbar { display: none; }
.px-slides__item { scroll-snap-align: start; }
.px-slides__card { display: grid; grid-template-columns: 1.6fr 1fr; gap: clamp(1rem, 3vw, 2.5rem); align-items: center; color: inherit; text-decoration: none; }
.px-slides__photo { display: block; aspect-ratio: 16 / 10; overflow: hidden; background: var(--t-surface-2); }
.px-slides__photo img { width: 100%; height: 100%; object-fit: cover; display: block; }
.px-slides__words { display: flex; flex-direction: column; gap: .6rem; }
.px-slides__title { margin: 0; }
.px-slides__nav { display: flex; align-items: center; justify-content: center; gap: 1rem; margin-top: 1rem; }
.px-slides__btn { width: 44px; height: 44px; border-radius: 999px; border: 1.5px solid var(--t-line); background: var(--t-surface); color: var(--t-ink); font-size: 1.3rem; cursor: pointer; }

@media (max-width: 900px) {
  .px-cards--grid { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  .px-cards--masonry { columns: 2; }
  .px-cards--grid[data-count="1"] .px-card { flex-direction: column; }
}
@media (max-width: 600px) {
  .px-cards--grid, .px-cards--grid[data-count="2"] { grid-template-columns: 1fr; }
  .px-cards--masonry { columns: 1; }
  .px-cards--justified .px-cards__item { flex-basis: 100%; }
  .px-port[data-layout="list"] .px-list__link { padding-right: 0; grid-template-columns: 72px 1fr; grid-template-rows: auto auto; column-gap: .9rem; row-gap: .15rem; align-items: center; }
  .px-list__thumb { display: block; grid-row: span 2; width: 72px; aspect-ratio: 1; overflow: hidden; }
  .px-list__thumb img { width: 100%; height: 100%; object-fit: cover; }
  .px-list__peek { display: none; }
  .px-slides__card { grid-template-columns: 1fr; }
}
@media (prefers-reduced-motion: reduce) {
  .px-card__photo img, .px-list__peek { transition: none; }
}
</style>
